import React, { PureComponent } from 'react';
import moment from 'moment/moment';
import Dayzed from 'dayzed';
import { TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import AnchorButton from '@adsk/bim360-matrix-react-anchor-button';
import ToggleButton from '@adsk/bim360-matrix-react-toggle-button';
import NavigationArrow from '@adsk/bim360-matrix-react-navigation-arrow';
import callOrReturn from '@adsk/bim360-matrix-react-helpers/es/callOrReturn';
import DayCell from './DayCell';
import { dateInputSharedProps, isSameDate } from './helpers/common';

import './_DateSelectorPopup.scss';
import DateSelectorMonthsView from './DateSelectorViews/DateSelectorMonthsView';
import DateSelectorYearsView from './DateSelectorViews/DateSelectorYearsView';
import { getSumOrZero } from './utils';
import { Themes } from '@adsk/bim360-matrix-react-helpers/es/theme';

const initDate = date => {
  if (!date) {
    return null;
  }
  return moment(date)
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    .toDate();
};

export const VIEWS = {
  DAYS: 'days',
  MONTHS: 'months',
  YEARS: 'years'
};

/**
 * DatePickerPopup
 */
class DateSelectorPopup extends PureComponent {
  constructor(props) {
    super(props);

    const parsedSelectedValue = initDate(props.selectedValue);

    this.state = {
      style: null,
      hoveredDate: null,
      selectedDates: props.range ? null : parsedSelectedValue,
      date: props.range ? props.calendarDate : parsedSelectedValue || props.calendarDate,
      minDate: this.getMinDateFromProps(),
      maxDate: this.getMinDateFromProps(),
      actions: props.actions || [],
      view: props.range ? VIEWS.DAYS : props.initialView,
      yearRange: {
        min: props.calendarDate.getFullYear() - 6,
        max: props.calendarDate.getFullYear() + 6
      }
    };

    this._renderHeader = this._renderHeader.bind(this);
    this._renderClearButton = this._renderClearButton.bind(this);
    this._renderFooter = this._renderFooter.bind(this);
    this.onChange = this.onChange.bind(this);
    this._setHoverDate = this._setHoverDate.bind(this);
    this.monthsStrings = new Array(12).fill(0).map((_, index) =>
      moment()
        .month(index)
        .format(props.monthFormat)
    );
  }

  /**
   * updates the date values on the state according to props
   */
  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      selectedValue,
      range,
      startDate,
      endDate,
      minDate,
      maxDate,
      actions,
      calendarDate,
      style
    } = nextProps;
    const newState = {};

    const parsedSelectedValue = initDate(selectedValue);

    if (
      (range && !this.state.selectedDates) ||
      !isSameDate(startDate, this.props.startDate) ||
      !isSameDate(endDate, this.props.endDate)
    ) {
      newState.selectedDates = [initDate(startDate), initDate(endDate)];
      newState.date = startDate ? new Date(startDate) : calendarDate;
    } else if (
      !range &&
      !isSameDate(
        this.state.selectedDates ? moment(this.state.selectedDates) : this.state.selectedDates,
        parsedSelectedValue ? moment(parsedSelectedValue) : parsedSelectedValue
      )
    ) {
      newState.selectedDates = initDate(selectedValue);
      newState.date = selectedValue ? initDate(selectedValue) : calendarDate;
    }
    if (!isSameDate(minDate, this.state.minDate)) {
      newState.minDate = this.getMinDateFromProps(nextProps);
    }
    if (!isSameDate(maxDate, this.state.maxDate)) {
      newState.maxDate = this.getMaxDateFromProps(nextProps);
    }

    if (actions) {
      if (actions !== this.state.actions) {
        newState.actions = actions;
      }
      const selectedAction = actions.find(action => action.isSelected()) || { id: null };
      if (selectedAction.id !== this.state.selectedAction) {
        newState.selectedAction = selectedAction.id;
      }
    }
    if (style && style !== this.state.style) {
      newState.style = style;
    }

    if (nextProps.range) {
      // reset view to days if range is on.
      newState.view = VIEWS.DAYS;
    }

    this.setState(newState);
  }

  getMinDateFromProps(props = this.props) {
    return props.minDate
      ? initDate(props.minDate)
      : props.disablePastDates
        ? initDate(new Date())
        : null;
  }

  getMaxDateFromProps(props = this.props) {
    return props.maxDate ? initDate(props.maxDate) : props.disableFutureDates ? new Date() : null;
  }

  getHeaderText = calendar => {
    const { range } = this.props;
    const { view, yearRange } = this.state;

    if (view === VIEWS.DAYS || range) {
      return `${this.monthsStrings[calendar.month]} ${calendar.year}`;
    } else if (view === VIEWS.MONTHS) {
      return calendar.year;
    } else if (view === VIEWS.YEARS) {
      return `${yearRange.min} - ${yearRange.max - 1}`;
    }

    return '';
  };

  getOverridenArrowBehavior = dir => {
    const { view } = this.state;
    const { range } = this.props;

    if (range) {
      return {
        onClick: () => this.setMonth(false, dir)
      };
    }

    if (view === VIEWS.DAYS) {
      return {
        onClick: () => this.setMonth(false, dir)
      };
    } else if (view === VIEWS.MONTHS) {
      return {
        onClick: () => this.setYear(false, dir)
      };
    } else if (view === VIEWS.YEARS) {
      return {
        onClick: () => this.setYearRange(dir)
      };
    }

    return {};
  };

  getMonthsByLocale = () => {
    const monthsShort = [
      this.props[TSymbolProp].translate('JAN'),
      this.props[TSymbolProp].translate('FEB'),
      this.props[TSymbolProp].translate('MAR'),
      this.props[TSymbolProp].translate('APR'),
      this.props[TSymbolProp].translate('MAY'),
      this.props[TSymbolProp].translate('JUN'),
      this.props[TSymbolProp].translate('JUL'),
      this.props[TSymbolProp].translate('AUG'),
      this.props[TSymbolProp].translate('SEP'),
      this.props[TSymbolProp].translate('OCT'),
      this.props[TSymbolProp].translate('NOV'),
      this.props[TSymbolProp].translate('DEC')
    ];

    return monthsShort;
  };

  setYearRange = dir => {
    if (dir === 'next') {
      this.setState(state => ({
        yearRange: {
          min: state.yearRange.min + 12,
          max: state.yearRange.max + 12
        }
      }));
    } else {
      this.setState(state => ({
        yearRange: {
          min: getSumOrZero(state.yearRange.min, -12),
          max: getSumOrZero(state.yearRange.max, -12)
        }
      }));
    }
  };

  updateMonthAndYear = (nextMonth, nextYear) => {
    const { date } = this.state;

    // create a new date, based on current viewing date and requested changes
    const nextDate = new Date(
      nextYear || date.getFullYear(),
      typeof nextMonth === 'number' ? nextMonth : date.getMonth(),
      1
    );

    this.setState({ date: nextDate }, () => {
      this.props.onMonthChanged({
        date: nextDate
      });
      this.props.scheduleUpdate();
    });
  };

  setYear = (nextYear, dir) => {
    const { view, date } = this.state;

    if (!nextYear && dir) {
      const currentYear = date.getFullYear();
      this.updateMonthAndYear(
        undefined,
        dir === 'next' ? currentYear + 1 : getSumOrZero(currentYear, -1)
      );
    } else {
      this.updateMonthAndYear(undefined, nextYear);
      if (view === VIEWS.YEARS) this.toggleView(VIEWS.MONTHS);
    }
  };

  setMonth = (nextMonth, dir) => {
    const { view } = this.state;

    if (typeof nextMonth !== 'number' && dir) {
      const dateToMutate = moment(this.state.date)
        .startOf('month')
        .add(dir === 'next' ? 1 : -1, 'months')
        .toDate();

      this.updateMonthAndYear(dateToMutate.getMonth(), dateToMutate.getFullYear());
    } else {
      this.updateMonthAndYear(nextMonth);
      if (view === VIEWS.MONTHS) this.toggleView(VIEWS.DAYS);
    }
  };

  toggleView = forceView => {
    const { view } = this.state;
    const { range } = this.props;

    // disable toggleView when navigationMode is off.
    if (range) return;

    if (forceView) {
      this.setState({ view: forceView });
    } else {
      if (view === VIEWS.DAYS) {
        this.setState({ view: VIEWS.MONTHS });
      } else if (view === VIEWS.MONTHS) {
        this.setState({ view: VIEWS.YEARS });
      } else if (view === VIEWS.YEARS) {
        this.setState({ view: VIEWS.DAYS });
      }
    }
  };

  /**
   * renders the header part of the calendar popup
   * @param calendar
   * @param calendars
   * @param getBackProps
   * @param getForwardProps
   * @param monthFormat
   * @returns {*}
   * @private
   */
  _renderHeader({
    calendar,
    calendars,
    getBackProps,
    getForwardProps,
    monthFormat,
    weekdayFormat
  }) {
    const { view, yearRange } = this.state;
    const { range } = this.props;

    const headerClasses = classNames('DateSelectorPopup__title', {
      'DateSelectorPopup__title--button': !range
    });

    // the key={this.state.yearRange.min} helps re render the arrows when navigating through year ranges.
    // removing this line will cause design break such as active elements due to the component not rerendering
    return (
      <div key={yearRange.min}>
        <div className="DateSelectorPopup__header">
          <NavigationArrow
            {...getBackProps({
              calendars,
              className: 'DateSelectorPopup__navigation-arrow',
              isPrevious: true,
              dataTestid: 'DateSelectorPopup__previous'
            })}
            {...this.getOverridenArrowBehavior('prev')}
          />
          <div
            onClick={() => this.toggleView()}
            className={headerClasses}
            data-testid="DateSelectorPopup__toggleView"
          >
            {this.getHeaderText(calendar)}
          </div>
          <NavigationArrow
            {...getForwardProps({
              calendars,
              className: 'DateSelectorPopup__navigation-arrow',
              dataTestid: 'DateSelectorPopup__next'
            })}
            {...this.getOverridenArrowBehavior('next')}
          />
        </div>
        {view === VIEWS.DAYS &&
          moment.weekdays().map((_, index) => (
            <div
              key={`${calendar.month}${calendar.year}${index}`}
              className="DateSelectorPopup__cell"
            >
              {moment()
                .weekday(index)
                .format(weekdayFormat)}
            </div>
          ))}
      </div>
    );
  }

  _renderFooter() {
    const { canClear, renderBottom } = this.props;
    const { actions } = this.state;
    if (!canClear && !actions.length && !renderBottom) {
      return null;
    } else {
      return (
        <div>
          {renderBottom && renderBottom()}
          {actions.length ? <div className="DateSelectorPopup__divider" /> : null}
          {actions.length ? this._renderActions() : null}
          {canClear && <div className="DateSelectorPopup__divider" />}
          {canClear && this._renderClearButton()}
        </div>
      );
    }
  }

  /**
   * renders the inner actions section in the calendar popup
   * @returns {*}
   * @private
   */
  _renderActions() {
    return (
      <div className="DateSelectorPopup__actions">
        {this.state.actions.map(action => (
          <ToggleButton
            key={action.id}
            label={action.label}
            checked={action.id === this.state.selectedAction}
            disabled={action.disabled}
            data-testid={`DateSelector__action${action.id}`}
            onChange={checked => {
              this.setState({ selectedAction: checked ? action.id : null });
              action.onToggle(checked);
            }}
          />
        ))}
      </div>
    );
  }

  /**
   * render the bottom section of the calendar popup
   * @returns {*}
   * @private
   */
  _renderClearButton() {
    return (
      <div className="DateSelectorPopup__bottom">
        <AnchorButton
          className="DateSelectorPopup__clear"
          onClick={this.props.onClear}
          disabled={this.props.clearDisabled}
          data-testid="DateSelectorPopup__clear"
        >
          {this.props.clearLabel || this.props[TSymbolProp].translate('CLEAR_DATES')}
        </AnchorButton>
      </div>
    );
  }

  /**
   * sets the hovered date on the state
   * @param date - the hovered date
   * @private
   */
  _setHoverDate(date) {
    const { range, startDate, endDate } = this.props;
    if (range && startDate && !endDate && !isSameDate(date, startDate)) {
      this.setState({ hoveredDate: date });
    } else {
      this.setState({ hoveredDate: null });
    }
  }

  onChange(data) {
    const { view } = this.state;

    if (!data.selectable) {
      return;
    }

    const dataWithView = { ...data, view };

    this.props.onChange(dataWithView);
  }

  _renderDayCell({ calendar, getDateProps, dateObj, index }) {
    const {
      tooltipContentForDate,
      range,
      endDate,
      startDate,
      isDateSelectable,
      popupContainer,
      dayClassName
    } = this.props;
    if (!dateObj) {
      return (
        <div
          key={`${calendar.year}${calendar.month}${index}`}
          className="DateSelectorPopup__placeholder"
        />
      );
    }
    const { date, selected, selectable, today } = dateObj;
    const rangeEnd = endDate || this.state.hoveredDate;
    const parsedDate = moment(date);
    const isSelectable = range
      ? selectable
      : selectable && (!isDateSelectable || isDateSelectable({ date }));
    const reverseRange = startDate && startDate > rangeEnd;
    const firstDate = reverseRange ? rangeEnd : startDate;
    const secondDate = reverseRange ? startDate : rangeEnd;

    //about in range parameters, the [] parameter indicates to include the start and end dates in the range for more info https://momentjs.com/docs/#/query/is-between/
    const inRange =
      firstDate &&
      secondDate &&
      parsedDate.isBetween(
        new Date(firstDate).setHours(0, 0, 0, 0),
        new Date(secondDate).setHours(0, 0, 0, 0),
        null,
        '[]'
      ) &&
      !isSameDate(moment(startDate), moment(endDate));
    const customDayClass = callOrReturn(dayClassName, { date });
    const cellClass = classNames('DateSelectorPopup__day', customDayClass, {
      'DateSelectorPopup__day--selected': selected,
      'DateSelectorPopup__day--is-end': isSameDate(parsedDate, moment(endDate)),
      'DateSelectorPopup__day--today': today,
      'DateSelectorPopup__day--in-range': inRange,
      'DateSelectorPopup__day--in-range-last': inRange && isSameDate(parsedDate, moment(rangeEnd)),
      'DateSelectorPopup__day--in-range-first':
        inRange && isSameDate(parsedDate, moment(startDate)),
      'DateSelectorPopup__day--hovered': isSameDate(parsedDate, moment(this.state.hoveredDate)),
      'DateSelectorPopup__day--reversed-range': reverseRange,
      'DateSelectorPopup__day--unselectable': !isSelectable
    });

    const tooltipContent = tooltipContentForDate ? tooltipContentForDate({ date }) : null;
    return (
      <DayCell
        className={cellClass}
        onMouseOver={isSelectable ? this._setHoverDate : null}
        date={date}
        key={`${date.toDateString()}`}
        dateObj={{ ...dateObj, selectable: isSelectable }}
        getDateProps={getDateProps}
        tooltipContent={tooltipContent}
        tooltipContainer={popupContainer}
      />
    );
  }

  renderView = ({ calendar, date, getDateProps }) => {
    const { view, minDate, maxDate } = this.state;
    const { range } = this.props;

    if (view === VIEWS.DAYS || range) {
      return calendar.weeks.map(week =>
        week.map((dateObj, index) =>
          this._renderDayCell({ calendar, date, getDateProps, dateObj, index })
        )
      );
    } else if (view === VIEWS.MONTHS && !range) {
      return (
        <DateSelectorMonthsView
          minDate={minDate}
          maxDate={maxDate}
          onMonthChange={this.setMonth}
          selectedMonth={date.getMonth()}
          months={this.getMonthsByLocale()}
          selectedYear={date.getFullYear()}
        />
      );
    } else if (view === VIEWS.YEARS && !range) {
      return (
        <DateSelectorYearsView
          minDate={minDate}
          maxDate={maxDate}
          onYearChange={this.setYear}
          yearRange={this.state.yearRange}
          selectedYear={date.getFullYear()}
        />
      );
    }

    return null;
  };

  /**
   * render function
   * @returns {*}
   */
  render() {
    const { monthFormat, weekdayFormat, className, overlayProps, theme } = this.props;
    const { minDate, maxDate, selectedDates, date } = this.state;

    return (
      <div {...overlayProps} className="DateSelectorPopup__container">
        <Dayzed
          onDateSelected={this.onChange}
          selected={selectedDates}
          minDate={minDate}
          maxDate={maxDate}
          date={date}
          firstDayOfWeek={moment.localeData().firstDayOfWeek()}
          render={({ calendars, getBackProps, getForwardProps, getDateProps }) => {
            return (
              <div
                className={classNames('DateSelectorPopup', className, {
                  [`DateSelectorPopup--${theme}`]: theme
                })}
              >
                {calendars.map(calendar => (
                  <div
                    key={`${calendar.month}${calendar.year}`}
                    className="DateSelectorPopup__month"
                  >
                    {this._renderHeader({
                      calendar,
                      calendars,
                      getBackProps,
                      getForwardProps,
                      monthFormat,
                      weekdayFormat
                    })}
                    {this.renderView({
                      calendar,
                      date,
                      getDateProps
                    })}
                  </div>
                ))}
                {this._renderFooter()}
              </div>
            );
          }}
        />
      </div>
    );
  }
}

DateSelectorPopup.propTypes = {
  ...dateInputSharedProps,
  /**
   * A list of custom actions to show that should correspond to date values.
   */
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * The label to show for the action
       */
      label: PropTypes.string.isRequired,
      /**
       * The action to do when toggling that action
       * (isSelected) => {}
       */
      onToggle: PropTypes.func.isRequired,
      /**
       * Callback that's invoked to determine if the action is selected.
       * When range === true => (startDate, endDate) => {}
       * When range === false => (date) => {}
       */
      isSelected: PropTypes.func
    })
  ),
  /**
   * format of how to display the month parameters
   */
  monthFormat: PropTypes.string,
  /**
   * how to format the weekdays
   */
  weekdayFormat: PropTypes.string,
  /**
   * callback to be called when a date has been selected
   */
  onChange: PropTypes.func,
  /**
   * callback to be called when clicking the clear button
   */
  onClear: PropTypes.func,
  /**
   * Label for the clear dates button
   */
  clearLabel: PropTypes.string,
  /**
   * whether to show 'clear dates' button
   */
  canClear: PropTypes.bool,
  /**
   * the style object injected from the overlay
   */
  style: PropTypes.object,
  /**
   * addtional class for the popup
   */
  className: PropTypes.string,
  /**
   * The default date to open the calendar on when there's no date selected (date or startDate)
   */
  calendarDate: PropTypes.instanceOf(Date),
  /**
   * Function to be called after an update of the overlay render is required.
   */
  scheduleUpdate: PropTypes.func,
  /**
   * Function to determine whether date is selectable. Not applicable to ranged DateSelector.
   * The handler is of shape ({ date }) => Boolean
   */
  isDateSelectable: PropTypes.func,
  /**
   * Function to be called whenever the user clicks "back" or "forward" on the month selector
   * The handler is of shape ({ date }) => void
   */
  onMonthChanged: PropTypes.func,
  /**
   * Controls the clear dates button disabled state
   */
  clearDisabled: PropTypes.bool,
  /**
   * Function to be called whenever date should have a tooltip or not, function return value should be a string
   */
  tooltipContentForDate: PropTypes.func,
  /**
   * Custom class name for day cell
   * The handler is of shape ({ date }) => string
   */
  dayClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  /**
   * Renders custom content at the bottom of the date selector
   * The handler is of shape () => node
   */
  renderBottom: PropTypes.func,
  /**
   * Sets the initial picker view.
   */
  initialView: PropTypes.oneOf(Object.values(VIEWS)),
  /** theme passed from DateSelector */
  theme: PropTypes.oneOf(Object.values(Themes))
};

DateSelectorPopup.defaultProps = {
  canClear: true,
  monthFormat: 'MMMM',
  weekdayFormat: 'dd',
  calendarDate: new Date(),
  initialView: VIEWS.DAYS,
  onMonthChanged: () => {}
};

export default DateSelectorPopup;
